import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
  useGetVisibleRechargePlansQuery,
  useCreateRechargePlanMutation,
  useUpdateRechargePlanMutation,
  useDeleteRechargePlanMutation,
} from "../../redux/admin/rechargePlanApi";

import { useGetUserProfileQuery } from "../../redux/admin/userApi";

const ROLE_OPTIONS = [
  "manager",
  "sub_admin",
  "group_admin",
  "client",
  "user",
  "viewer",
  "driver",
];

const formatRoleLabel = (value = "") =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const RechargePlans = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: profile } = useGetUserProfileQuery();
  const role = profile?.role;
  const directBillingOwner = profile?.directBillingOwner || null;

  const isAdminLike = role === "admin" || role === "manager";

  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetVisibleRechargePlansQuery();

  const [createPlan, { isLoading: creating }] =
    useCreateRechargePlanMutation();
  const [updatePlan, { isLoading: updating }] =
    useUpdateRechargePlanMutation();

  const [deletePlan] = useDeleteRechargePlanMutation();

  const plans = data?.plans || [];

  // modal
  const [open, setOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState("");

  // form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [planFor, setPlanFor] = useState("");
  const [amount, setAmount] = useState("");
  const [offer, setOffer] = useState("");
  const [validity, setValidity] = useState("");
  const [status, setStatus] = useState(true);
  const [visibleRoles, setVisibleRoles] = useState([]);
  const [formErr, setFormErr] = useState("");

  const resetForm = () => {
    setEditingPlanId("");
    setName("");
    setDescription("");
    setPlanFor("");
    setAmount("");
    setOffer("");
    setValidity("");
    setStatus(true);
    setVisibleRoles([]);
    setFormErr("");
  };

  const canSubmit = useMemo(() => {
    return name && amount && validity;
  }, [name, amount, validity]);

  const isEditing = Boolean(editingPlanId);

  const handleSave = async () => {
    try {
      if (!canSubmit) {
        setFormErr("Name, Amount aur Validity required hai");
        return;
      }

      const payload = {
        name,
        description,
        planFor,
        amount: Number(amount),
        offer,
        validityDays: Number(validity),
        status,
        ...(isAdminLike ? { visibleForRoles: visibleRoles } : {}),
      };

      if (isEditing) {
        await updatePlan({
          id: editingPlanId,
          ...payload,
        }).unwrap();
      } else {
        await createPlan(payload).unwrap();
      }

      resetForm();
      setOpen(false);
    } catch (e) {
      setFormErr(e?.data?.message || `Plan ${isEditing ? "update" : "create"} failed`);
    }
  };

  const handleEditOpen = (plan) => {
    setEditingPlanId(plan._id);
    setName(plan.name || "");
    setDescription(plan.description || "");
    setPlanFor(plan.planFor || "");
    setAmount(plan.amount ?? "");
    setOffer(plan.offer || "");
    setValidity(plan.validityDays ?? "");
    setStatus(Boolean(plan.status));
    setVisibleRoles(Array.isArray(plan.visibleForRoles) ? plan.visibleForRoles : []);
    setFormErr("");
    setOpen(true);
  };

  const handleCreateOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleCloseModal = () => {
    resetForm();
    setOpen(false);
  };

  const handleDelete = async (planId) => {
    const confirmed = window.confirm("Delete this recharge plan?");
    if (!confirmed) return;
    try {
      await deletePlan(planId).unwrap();
    } catch (e) {
      setFormErr(e?.data?.message || "Plan delete failed");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Recharge Plans
        </Typography>

        {profile?.userPermissions?.create && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateOpen}
            sx={{ fontWeight: 800, borderRadius: 2 }}
          >
            Add Plan
          </Button>
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(25, 118, 210, 0.04)",
        }}
      >
        <Typography fontWeight={800} sx={{ mb: 0.5 }}>
          Plan visibility rule
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {directBillingOwner?._id
            ? `Aap apne khud ke plans ke saath ${directBillingOwner.username || "parent owner"} ke recharge plans dekh rahe ho. Neeche wale roles ko sirf nearest parent owner ke plans hi dikhte hain.`
            : "Aapke khud ke recharge plans visible hain. Neeche wale roles ko sirf nearest parent owner ke plans hi dikhte hain."}
        </Typography>
      </Paper>

      {/* Content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Divider />

        {isLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {error?.data?.message || "Failed to load plans"}
            </Alert>
          </Box>
        ) : plans.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography fontWeight={800}>No Recharge Plans</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ p: 2 }}>
            {plans.map((p) => (
              <Paper
                key={p._id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack spacing={0.5}>
                    <Typography fontWeight={900}>{p.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {p.description || "—"}
                    </Typography>

                    <Stack direction="row" spacing={1} mt={1}>
                      <Chip
                        size="small"
                        label={`₹${p.amount}`}
                        sx={{ fontWeight: 800 }}
                      />
                      <Chip
                        size="small"
                        label={`${p.validityDays} Days`}
                        sx={{ fontWeight: 800 }}
                      />
                      {p.offer && (
                        <Chip
                          size="small"
                          color="success"
                          label={p.offer}
                          sx={{ fontWeight: 800 }}
                        />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`By ${String(p.createdByRole || "").replaceAll("_", " ") || "user"}`}
                        sx={{ fontWeight: 700, textTransform: "capitalize" }}
                      />
                      {p.planFor ? (
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          label={`Plan For: ${p.planFor}`}
                          sx={{ fontWeight: 700 }}
                        />
                      ) : null}
                    </Stack>
                    {Array.isArray(p.visibleForRoles) && p.visibleForRoles.length ? (
                      <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                        {p.visibleForRoles.map((roleValue) => (
                          <Chip
                            key={`${p._id}-${roleValue}`}
                            size="small"
                            variant="outlined"
                            label={formatRoleLabel(roleValue)}
                            sx={{ fontWeight: 700 }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {p.status ? (
                      <Chip size="small" color="success" label="Active" />
                    ) : (
                      <Chip size="small" color="default" label="Inactive" />
                    )}

                    {p.canEdit ? (
                      <IconButton
                        color="primary"
                        onClick={() => handleEditOpen(p)}
                      >
                        <EditIcon />
                      </IconButton>
                    ) : null}

                    {p.canDelete ? (
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(p._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Create Modal */}
      <Dialog
        open={open}
        onClose={handleCloseModal}
        fullScreen={isSm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={900}>
              {isEditing ? "Edit Recharge Plan" : "New Recharge Plan"}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5}>
            {formErr && <Alert severity="error">{formErr}</Alert>}

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
            <TextField
              label="Plan For"
              value={planFor}
              onChange={(e) => setPlanFor(e.target.value)}
              fullWidth
              placeholder="Example: Sub Admin, Dealer, Field Team, GPS Devices"
            />
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
            />
            <TextField
              label="Offer"
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              fullWidth
            />
            <TextField
              label="Validity (in Days)"
              type="number"
              value={validity}
              onChange={(e) => setValidity(e.target.value)}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={status}
                  onChange={(e) => setStatus(e.target.checked)}
                />
              }
              label="Active"
            />

            {/* ✅ Role selection only for admin / Manager */}
            {isAdminLike && (
              <TextField
                select
                label="Visible For Roles"
                SelectProps={{ multiple: true }}
                value={visibleRoles}
                onChange={(e) => setVisibleRoles(e.target.value)}
                fullWidth
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {formatRoleLabel(r)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSubmit || creating || updating}
          >
            {creating || updating ? "Saving..." : isEditing ? "UPDATE" : "SAVE"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RechargePlans;
